import { Playbook } from "../../../types/playbook/playbook";
import { API, DEFAULT_PLAYBOOK_PAGE_SIZE, X_NUM_OF_PAGES, X_PAGE, X_TOTAL_ROWS } from "../../constants";
import { PagedResponse } from "../../types";
import { apiSlice } from "../api-slice";
import { CreatePlaybookPayload, PlaybookPDFCustomSettingsPayload, PlaybookResponse, PrintOptions } from "./types";
import { GetPlaybookQueryParams } from "./types/query-params";

const getPlaybookQueryString = ({ page = 0, pageSize = DEFAULT_PLAYBOOK_PAGE_SIZE }: GetPlaybookQueryParams) => {
  const pageStringified = `&page=${page.toString()}`;
  const pageSizeStringified = `&size=${pageSize.toString()}`;

  return `${pageStringified}${pageSizeStringified}`;
};

const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getAllPlaybooks: build.query<PagedResponse<Playbook[]>, GetPlaybookQueryParams>({
      query: ({ page = 0, pageSize = DEFAULT_PLAYBOOK_PAGE_SIZE }) => {
        const queryString = getPlaybookQueryString({ page, pageSize });

        return `${API}/playbooks?${queryString}`;
      },
      serializeQueryArgs: ({ queryArgs }) => {
        return getPlaybookQueryString(queryArgs);
      },
      transformResponse: (response: Playbook[], meta) => {
        const headers = meta?.response?.headers;
        const page = Number(headers?.get(X_PAGE)) || 0;
        const numOfPages = Number(headers?.get(X_NUM_OF_PAGES)) || 0;
        const totalRows = Number(headers?.get(X_TOTAL_ROWS)) || 0;

        return { page, numOfPages, totalRows, data: response };
      },
      providesTags: ["Playbook"],
    }),
    getPlaybook: build.query<Playbook, string>({
      query: (id) => `${API}/playbooks/${id}`,
      providesTags: ["Playbook"],
    }),
    getPrintSettingsOptions: build.query<PrintOptions, void>({
      query: () => `${API}/print-styles/options`,
    }),
    updatePlaybook: build.mutation<Partial<Playbook>, Partial<Playbook>>({
      query: (body) => ({
        url: `${API}/playbooks`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Playbook"],
    }),
    createPlaybook: build.mutation<PlaybookResponse, CreatePlaybookPayload>({
      query: (body) => ({
        url: `${API}/playbooks`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Playbook"],
    }),
    deletePlaybook: build.mutation<Playbook, string>({
      query: (id) => ({
        url: `${API}/playbooks/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Playbook"],
    }),
    createPlaybookPDF: build.mutation<Playbook, string>({
      query: (id) => ({
        url: `${API}/playbooks/${id}/?pdfFromWeb=true`,
      }),
    }),
    createPlaybookPDFWithCustomStyling: build.mutation<string, { id: string; body: PlaybookPDFCustomSettingsPayload }>({
      query: ({ id, body }) => {
        return {
          url: `${API}/playbooks/${id}/pdf-render`,
          method: "POST",
          body,
          responseHandler: (response: { text: () => any }) => response.text(),
        };
      },
    }),
  }),
});

export const {
  endpoints,
  useGetAllPlaybooksQuery,
  useLazyGetAllPlaybooksQuery,
  useGetPlaybookQuery,
  useGetPrintSettingsOptionsQuery,
  useLazyGetPlaybookQuery,
  useDeletePlaybookMutation,
  useCreatePlaybookPDFMutation,
  useCreatePlaybookMutation,
  useUpdatePlaybookMutation,
  useCreatePlaybookPDFWithCustomStylingMutation,
} = extendedApiSlice;
